import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Settings, Upload, FileText } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useServiceStaticFiles,
  useUploadServiceStaticFile,
  useDeleteServiceStaticFile,
  downloadServiceStaticFile,
  type StaticFile,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './WafSettingsTab.module.css';

const muted = (v: React.ReactNode) => <span className={styles.muted}>{v}</span>;

/** Managed file types for AI-WAF rules → display label. */
const FILE_TYPE_LABEL: Record<string, string> = {
  xml_protection_schema: 'XSD',
  xml_protection_soap: 'WSDL',
};

/** Pick the vendor `type` for an uploaded file by extension (XSD / WSDL only). */
const typeForFile = (name: string): string | null => {
  const lower = name.toLowerCase();
  if (lower.endsWith('.xsd')) return 'xml_protection_schema';
  if (lower.endsWith('.wsdl')) return 'xml_protection_soap';
  return null;
};

const formatTime = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

/** A file picked in the upload dialog, after extension validation. */
interface PickedFile {
  file: File;
  type: string;
}

/**
 * AI-WAF → Settings → File Management. Upload and manage the XSD / WSDL files that
 * AI-WAF rules (XML Schema / SOAP verification) reference. Backed by the shared
 * static-file endpoints; the list filters to the schema/WSDL file types.
 */
export function WafSettingsTab() {
  const { show } = useToast();
  // File Management is account-level (files scoped to the vendor customer), so it
  // resolves from the service — no website required.
  const { serviceId } = useParams<{ serviceId: string }>();

  const { data: allFiles = [], isLoading } = useServiceStaticFiles(serviceId);
  const files = useMemo(
    () => allFiles.filter((f) => f.type in FILE_TYPE_LABEL),
    [allFiles],
  );

  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<StaticFile | null>(null);
  /** Pending overwrite: the picked file plus the existing file it would replace. */
  const [pendingReplace, setPendingReplace] = useState<{ picked: PickedFile; existing: StaticFile } | null>(null);

  const upload = useUploadServiceStaticFile(serviceId ?? '', {
    onSuccess: (_d, vars) => {
      // track feature engagement
      trackFeatureAction('waap', 'ai_waf_static_file', 'create');
      show({ tone: 'success', title: `Uploaded ${vars.name}` });
      setUploadOpen(false);
      setPendingReplace(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Upload failed', description: errMessage(e) }),
  });
  const del = useDeleteServiceStaticFile(serviceId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'ai_waf_static_file', 'delete');
      show({ tone: 'success', title: 'File deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  /** Read the picked file's text and POST it (optionally replacing `id` in place). */
  const doUpload = async (picked: PickedFile, replaceId?: number) => {
    const content = await picked.file.text();
    upload.mutate({ content, name: picked.file.name, type: picked.type, id: replaceId ?? 0 });
  };

  /** Save handler from the upload dialog: replace in place when the name already exists. */
  const onSave = async (picked: PickedFile) => {
    const existing = allFiles.find((f) => f.name === picked.file.name && f.type === picked.type);
    if (existing) {
      setUploadOpen(false);
      setPendingReplace({ picked, existing });
      return;
    }
    await doUpload(picked);
  };

  const onDownload = async (file: StaticFile) => {
    try {
      await downloadServiceStaticFile(serviceId as string, file);
    } catch (e) {
      show({ tone: 'danger', title: 'Download failed', description: errMessage(e) });
    }
  };

  const columns = useMemo<ColumnDef<StaticFile>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className={styles.name}>{row.original.name}</span>,
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <StatusPill tone="neutral">{FILE_TYPE_LABEL[row.original.type] ?? row.original.type}</StatusPill>
        ),
      },
      {
        id: 'uploaded',
        header: 'Uploaded Time',
        cell: ({ row }) => muted(formatTime(row.original.created)),
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <div className={styles.actions}>
            <Button variant="ghost" size="sm" onClick={() => onDownload(row.original)}>
              Download
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPendingDelete(row.original)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'AI-WAF' }, { label: 'Settings' }]} />}
        title="Settings"
      />

      <Section
        title="File Management"
        description="Upload and manage the XSD and WSDL files that AI-WAF rules use. File names may contain 1-64 characters: A-Z, a-z, 0-9, _ and . (dot)."
        actions={
          <Button
            variant="secondary"
            startIcon={<Upload size={14} />}
            onClick={() => setUploadOpen(true)}
          >
            Upload
          </Button>
        }
      >
        {isLoading ? (
          <div className={styles.skeletonStack}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 48 }} />
            ))}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={files}
            emptyState={
              <EmptyState
                size="sm"
                icon={<Settings size={20} />}
                title="No files"
                description="Upload an XSD or WSDL file for your XML Schema / SOAP verification rules."
              />
            }
          />
        )}
      </Section>

      {uploadOpen && (
        <UploadFileDialog
          pending={upload.isPending}
          onClose={() => setUploadOpen(false)}
          onSave={onSave}
          onError={(msg) => show({ tone: 'danger', title: 'Unsupported file', description: msg })}
        />
      )}

      <ConfirmDialog
        open={!!pendingReplace}
        onOpenChange={(o) => !o && setPendingReplace(null)}
        title="Replace file?"
        description={`"${pendingReplace?.existing.name}" already exists. Replace it with the file you selected?`}
        confirmLabel="Replace"
        loading={upload.isPending}
        onConfirm={() => pendingReplace && doUpload(pendingReplace.picked, pendingReplace.existing.id)}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete file?"
        description={`Delete "${pendingDelete?.name}"? Rules referencing it may stop working. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => pendingDelete && del.mutate(pendingDelete.id)}
      />
    </Container>
  );
}

/** Modal to pick an XSD/WSDL file and save it. Validates the extension on pick. */
function UploadFileDialog({
  pending,
  onClose,
  onSave,
  onError,
}: {
  pending: boolean;
  onClose: () => void;
  onSave: (picked: PickedFile) => void;
  onError: (message: string) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<PickedFile | null>(null);

  const onPick = (file: File) => {
    const type = typeForFile(file.name);
    if (!type) {
      onError('Only .xsd and .wsdl files are allowed.');
      setPicked(null);
      return;
    }
    setPicked({ file, type });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload file</DialogTitle>
          <DialogDescription>
            Choose an XSD or WSDL file. If a file with the same name already exists, it will be replaced.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <input
            ref={fileInput}
            type="file"
            accept=".xsd,.wsdl"
            className={styles.hiddenInput}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onPick(f);
              e.target.value = '';
            }}
          />
          <Button variant="secondary" startIcon={<FileText size={14} />} onClick={() => fileInput.current?.click()}>
            Choose file
          </Button>
          {picked && (
            <div className={styles.pickedRow}>
              <span className={styles.name}>{picked.file.name}</span>
              <StatusPill tone="neutral">{FILE_TYPE_LABEL[picked.type]}</StatusPill>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={pending || !picked} onClick={() => picked && onSave(picked)}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
